import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import KPICard from '../../components/reports/KPICard';
import ReportChart from '../../components/reports/ReportChart';
import ReportTable from '../../components/reports/ReportTable';
import BranchSelect from '../../components/reports/BranchSelect';
import {
  calculateWarningKPIs,
  getTopRiskIndicators,
  calculateAging,
  getUniqueBranches,
  filterByBranch,
  exportToCSV
} from '../../utils/reportHelpers';

function HighRiskReport() {
  const navigate = useNavigate();
  const { warnings, customers } = useData();
  
  const [selectedBranch, setSelectedBranch] = useState([]);
  const branches = getUniqueBranches(customers);

  // Filter high risk warnings
  let highRiskWarnings = warnings.filter(w => w.riskLevel === 'high' && w.status !== 'resolved');
  highRiskWarnings = filterByBranch(highRiskWarnings, customers, selectedBranch);
  
  const kpis = calculateWarningKPIs(highRiskWarnings);
  const topIndicators = getTopRiskIndicators(highRiskWarnings);

  // Calculate aging statistics
  const agingData = {
    overdue: highRiskWarnings.filter(w => calculateAging(w).days > 30).length,
    warning: highRiskWarnings.filter(w => {
      const days = calculateAging(w).days;
      return days > 14 && days <= 30;
    }).length,
    normal: highRiskWarnings.filter(w => calculateAging(w).days <= 14).length
  };

  const tableColumns = [
    { key: 'code', label: 'Mã CB', sortable: true, width: '100px' },
    { key: 'customerName', label: 'Khách hàng', sortable: true },
    { key: 'score', label: 'Điểm', sortable: true, type: 'number', width: '80px' },
    { key: 'alertType', label: 'Loại cảnh báo' },
    { 
      key: 'aging', 
      label: 'Số ngày', 
      sortable: true,
      render: (_, row) => {
        const aging = calculateAging(row);
        return (
          <span className={`badge bg-${aging.days > 30 ? 'danger' : aging.days > 14 ? 'warning' : 'success'}`}>
            {aging.days} ngày
          </span>
        );
      }
    },
    { key: 'assignedTo', label: 'Người phụ trách' },
    { key: 'status', label: 'Trạng thái' },
    {
      key: 'actions',
      label: 'Hành động',
      render: (_, row) => (
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/warnings', { state: { selectedWarning: row } });
          }}
        >
          <i className="bi bi-eye"></i>
        </button>
      )
    }
  ];

  const handleExport = () => {
    const exportData = highRiskWarnings.map(w => ({
      'Mã': w.code,
      'Khách hàng': w.customerName,
      'Điểm': w.score,
      'Loại cảnh báo': w.alertType,
      'Số ngày tồn tại': calculateAging(w).days,
      'Người xử lý': w.assignedTo || 'Chưa phân công',
      'Trạng thái': w.status,
      'Ngày tạo': w.createdDate
    }));
    exportToCSV(exportData, 'bao-cao-rui-ro-cao');
  };

  return (
    <div className="container-fluid">
      {/* Header with Alert */}
      <div className="page-header-sticky">
        <div className="row mb-4">
          <div className="col-12">
            <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
            
            <div className="alert alert-danger border-start border-danger border-4" role="alert">
              <h4 className="alert-heading">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Cảnh báo Rủi ro Cao
              </h4>
              <p className="mb-0">
                Có <strong>{kpis.newWarnings}</strong> cảnh báo mới cần xử lý ngay. 
                {agingData.overdue > 0 && (
                  <span className="ms-2 badge bg-dark">{agingData.overdue} cases quá hạn 30 ngày</span>
                )}
              </p>
            </div>

            <h2>
              <i className="bi bi-exclamation-triangle-fill me-2 text-danger"></i>
              Báo cáo các trường hợp cảnh báo sớm rủi ro cao
            </h2>
            <p className="text-muted">
              Focus vào cases nguy hiểm cần xử lý ưu tiên
            </p>
          </div>
        </div>
      </div>

      {/* Branch Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <BranchSelect 
            value={selectedBranch}
            onChange={setSelectedBranch}
            branches={branches}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <KPICard 
            title="Tổng rủi ro cao"
            value={highRiskWarnings.length}
            icon="bi-exclamation-triangle-fill"
            color="danger"
            subtitle={`${kpis.newWarnings} mới`}
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Quá hạn 30 ngày"
            value={agingData.overdue}
            icon="bi-hourglass-split"
            color="danger"
            subtitle="Cần xử lý gấp"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Điểm trung bình"
            value={kpis.avgScore}
            icon="bi-speedometer2"
            color="warning"
            subtitle="Mức độ nghiêm trọng"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Đang xử lý"
            value={kpis.inProgress}
            icon="bi-person-check"
            color="info"
            subtitle={`${kpis.newWarnings} chưa phân công`}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-6">
          <ReportChart 
            type="bar"
            title="Top 5 Indicators gây rủi ro cao"
            data={topIndicators}
            height={300}
          />
        </div>
        <div className="col-md-6">
          <ReportChart 
            type="doughnut"
            title="Phân bố theo aging"
            data={{
              labels: ['Quá hạn (>30 ngày)', 'Cảnh báo (15-30 ngày)', 'Bình thường (<15 ngày)'],
              datasets: [{
                data: [agingData.overdue, agingData.warning, agingData.normal],
                backgroundColor: ['#dc3545', '#fd7e14', '#ffc107']
              }]
            }}
            height={300}
          />
        </div>
      </div>

      {/* Priority Matrix Info */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-lightbulb me-2"></i>
                Khuyến nghị xử lý
              </h5>
              <ul className="mb-0">
                <li>Ưu tiên cases có điểm &gt; 85 và tồn tại &gt; 14 ngày</li>
                <li>Liên hệ khách hàng trong vòng 24h để xác minh tình hình</li>
                <li>Xem xét điều chỉnh hạn mức hoặc thiết lập kế hoạch trả nợ mới</li>
                <li>Tăng tần suất theo dõi lên hàng tuần</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Danh sách chi tiết ({highRiskWarnings.length} bản ghi)</h4>
            <button className="btn btn-danger" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Xuất Excel
            </button>
          </div>
          <ReportTable 
            data={highRiskWarnings}
            columns={tableColumns}
            onRowClick={(row) => navigate('/warnings', { state: { selectedWarning: row } })}
            pageSize={15}
          />
        </div>
      </div>
    </div>
  );
}

export default HighRiskReport;
